import { isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { rawEnvelope } from '../http/envelopes';
import { ApiError } from '../http/errors';
import type { PropertyFaq } from './native-property-content';

/**
 * 공유 FAQ 모음 — `GET admin/content-collections/{id}`, `PUT admin/content-collections/{id}/items/{item}`.
 * 정본: core-admin-resource-fixes(PR182 head) app/modules/property/interfaces/http/native_content.py.
 * 명령은 현재 활성 배정 전체와 영향 지점 전체의 기대 차수를 그대로 제출한다. 목록 차수를 성공 근거로 쓰지 않는다.
 */
type FaqItem = PropertyFaq['faq_sets'][number]['items'][number];
export interface FaqCollection {
  id: string;
  name: string | null;
  revision: number;
  language: 'ko' | 'en' | 'und';
  sort_order: number;
  items: (FaqItem & { deleted_at: string | null })[];
  assignments: {
    id: string;
    property_id: string;
    revision: number;
    sort_order: number;
    display_state: 'VISIBLE' | 'HIDDEN';
    selection_mode: 'ALL' | 'EXPLICIT';
    deleted_at: string | null;
    item_ids: string[];
  }[];
  property_revisions: Record<string, number>;
}
export interface FaqItemReplace {
  command_id: string;
  expected_collection_revision: number;
  expected_assignment_revisions: Record<string, number>;
  expected_property_revisions: Record<string, number>;
  expected_item_revision: number;
  title: string | null;
  payload: FaqItem['payload'];
}
const record = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
const exact = (v: Record<string, unknown>, keys: string[]) =>
  Object.keys(v).length === keys.length && keys.every((k) => Object.hasOwn(v, k));
const positive = (v: unknown): v is number => Number.isSafeInteger(v) && Number(v) > 0;
const text = (v: unknown) => v === null || typeof v === 'string';
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '공유 FAQ 응답을 확인하지 못했습니다.',
  });
const revisionMap = (v: unknown): v is Record<string, number> =>
  record(v) && Object.entries(v).every(([k, n]) => isUuid(k) && positive(n));
const translations = (v: unknown) =>
  record(v) && exact(v, ['ko', 'en']) && text(v.ko) && text(v.en);
const payload = (v: unknown) =>
  record(v) &&
  exact(v, [
    'question',
    'answer',
    'detail',
    'apply_completed',
    'vacant_room',
    'membership_state',
    'source_order',
  ]) &&
  translations(v.question) &&
  translations(v.answer) &&
  ['detail', 'apply_completed', 'vacant_room'].every((k) => typeof v[k] === 'boolean') &&
  (v.membership_state === 'CURRENT' || v.membership_state === 'HISTORICAL') &&
  (v.source_order === null || Number.isSafeInteger(v.source_order));

/** `targetPropertyId`는 최초 배정·재활성화 대상 지점의 콘텐츠 차수를 property_revisions에 포함해 달라는 서버 조회 조건이다. */
export async function readFaqCollection(
  client: ServiceClient,
  collectionId: string,
  signal?: AbortSignal,
  targetPropertyId?: string,
): Promise<FaqCollection> {
  if (!isUuid(collectionId) || (targetPropertyId !== undefined && !isUuid(targetPropertyId)))
    throw invalid();
  const v = await client.get<unknown>(`admin/content-collections/${collectionId}`, {
    signal,
    ...(targetPropertyId ? { params: { target_property_id: targetPropertyId } } : {}),
  });
  if (
    !record(v) ||
    !exact(v, [
      'id',
      'name',
      'revision',
      'language',
      'sort_order',
      'items',
      'assignments',
      'property_revisions',
    ]) ||
    typeof v.id !== 'string' ||
    v.id.toLowerCase() !== collectionId.toLowerCase() ||
    !text(v.name) ||
    !positive(v.revision) ||
    !['ko', 'en', 'und'].includes(String(v.language)) ||
    !Number.isSafeInteger(v.sort_order) ||
    !revisionMap(v.property_revisions) ||
    !Array.isArray(v.items) ||
    !v.items.every(
      (item) =>
        record(item) &&
        exact(item, ['id', 'title', 'sort_order', 'payload', 'revision', 'deleted_at']) &&
        isUuid(item.id) &&
        text(item.title) &&
        Number.isSafeInteger(item.sort_order) &&
        positive(item.revision) &&
        payload(item.payload) &&
        text(item.deleted_at),
    ) ||
    !Array.isArray(v.assignments) ||
    !v.assignments.every(
      (a) =>
        record(a) &&
        exact(a, [
          'id',
          'property_id',
          'revision',
          'sort_order',
          'display_state',
          'selection_mode',
          'deleted_at',
          'item_ids',
        ]) &&
        isUuid(a.id) &&
        isUuid(a.property_id) &&
        positive(a.revision) &&
        ['VISIBLE', 'HIDDEN'].includes(String(a.display_state)) &&
        ['ALL', 'EXPLICIT'].includes(String(a.selection_mode)) &&
        text(a.deleted_at) &&
        Array.isArray(a.item_ids) &&
        a.item_ids.every(isUuid),
    )
  )
    throw invalid();
  return v as unknown as FaqCollection;
}

/** 현재 활성(해제되지 않은) 배정 전체와 영향 지점 차수로 만든 닫힌 기대값. 부분 map은 만들지 않는다. */
export function faqExpectations(collection: FaqCollection) {
  const active = collection.assignments.filter((a) => a.deleted_at === null);
  return {
    expected_collection_revision: collection.revision,
    expected_assignment_revisions: Object.fromEntries(active.map((a) => [a.id, a.revision])),
    expected_property_revisions: { ...collection.property_revisions },
  };
}

type Expectations = ReturnType<typeof faqExpectations>;
export interface FaqPayloadInput {
  title: string | null;
  payload: FaqItem['payload'];
}
/** 한 번의 쓰기 명령. 대상 경로와 서버 DTO를 함께 담아 같은 요청을 그대로 재전송할 수 있다. */
export type FaqCommand =
  | {
      op: 'item-create';
      collectionId: string;
      body: { command_id: string; kind: 'FAQ' } & Expectations & FaqPayloadInput;
    }
  | { op: 'item-replace'; collectionId: string; itemId: string; body: FaqItemReplace }
  | {
      op: 'item-order';
      collectionId: string;
      body: { command_id: string; item_ids: string[] } & Expectations;
    }
  | {
      op: 'assign';
      collectionId: string;
      body: {
        command_id: string;
        scope_kind: 'PROPERTY';
        property_id: string;
        purpose: 'FAQ';
        sort_order: number;
        reactivate: boolean;
        display_state: 'VISIBLE' | 'HIDDEN';
        selection_mode: 'ALL' | 'EXPLICIT';
        item_ids: string[];
      } & Expectations;
    }
  | {
      op: 'selection';
      assignmentId: string;
      body: {
        command_id: string;
        display_state: 'VISIBLE' | 'HIDDEN';
        selection_mode: 'ALL' | 'EXPLICIT';
        item_ids: string[];
      } & Expectations;
    }
  | { op: 'retire'; assignmentId: string; body: { command_id: string } & Expectations };

function checkResult(v: unknown, commandId: string): asserts v is Record<string, unknown> {
  if (
    !record(v) ||
    !exact(v, [
      'command_id',
      'target_kind',
      'target_id',
      'result_revision',
      'collection_id',
      'collection_revision',
      'assignment_revisions',
      'property_revisions',
      'item_id',
      'http_status',
    ]) ||
    v.command_id !== commandId ||
    !isUuid(v.target_id) ||
    !isUuid(v.collection_id) ||
    !positive(v.result_revision) ||
    !positive(v.collection_revision) ||
    !revisionMap(v.assignment_revisions) ||
    !revisionMap(v.property_revisions) ||
    (v.item_id !== null && !isUuid(v.item_id)) ||
    ![200, 201].includes(Number(v.http_status))
  )
    throw invalid();
}

/** 쓰기 한 건을 닫힌 경로로 보내고 응답이 같은 명령의 결과인지만 확인한다. 화면 성공은 이후 다시 읽은 값으로 판단한다. */
export async function sendFaqCommand(client: ServiceClient, command: FaqCommand): Promise<void> {
  let v: unknown;
  const ids = [
    'collectionId' in command ? command.collectionId : null,
    'itemId' in command ? command.itemId : null,
    'assignmentId' in command ? command.assignmentId : null,
  ];
  if (ids.some((id) => id !== null && !isUuid(id))) throw invalid();
  switch (command.op) {
    case 'item-create':
      v = await client.post<unknown>(`admin/content-collections/${command.collectionId}/items`, {
        json: command.body,
      });
      break;
    case 'item-replace':
      v = await client.put<unknown>(
        `admin/content-collections/${command.collectionId}/items/${command.itemId}`,
        { json: command.body },
      );
      if (record(v) && v.item_id !== command.itemId) throw invalid();
      break;
    case 'item-order':
      v = await client.put<unknown>(
        `admin/content-collections/${command.collectionId}/item-order`,
        {
          json: command.body,
        },
      );
      break;
    case 'assign':
      v = await client.post<unknown>(
        `admin/content-collections/${command.collectionId}/assignments`,
        { json: command.body },
      );
      break;
    case 'selection':
      v = await client.put<unknown>(`admin/content-assignments/${command.assignmentId}/selection`, {
        json: command.body,
      });
      break;
    case 'retire':
      v = await client.post<unknown>(
        `admin/content-assignments/${command.assignmentId}/retirements`,
        { json: command.body },
      );
      break;
  }
  checkResult(v, command.body.command_id);
}

export function replaceFaqItem(
  client: ServiceClient,
  collectionId: string,
  itemId: string,
  body: FaqItemReplace,
): Promise<void> {
  return sendFaqCommand(client, { op: 'item-replace', collectionId, itemId, body });
}

export interface FaqCollectionCreate {
  command_id: string;
  owner_scope_kind: 'SHARED_FAQ';
  purpose: 'FAQ';
  language: 'ko' | 'en' | 'und';
  name: string | null;
  sort_order: number;
}
/** 새 공유 FAQ 모음을 만든다. 원본(source) 정보는 보내지 않는다. 서버가 발급한 collection UUID를 돌려준다. */
export async function createFaqCollection(
  client: ServiceClient,
  body: FaqCollectionCreate,
): Promise<string> {
  const v = await client.post<unknown>('admin/content-collections', { json: body });
  checkResult(v, body.command_id);
  if (v.target_kind !== 'CONTENT_COLLECTION' || v.target_id !== v.collection_id) throw invalid();
  return v.collection_id as string;
}

export interface FaqCollectionSummary {
  id: string;
  name: string | null;
  revision: number;
  language: 'ko' | 'en' | 'und';
  sort_order: number;
}
/** 배정 여부와 관계없이 살아 있는 공유 모음을 UUID 순서 페이지로 모두 찾는다. */
export async function listFaqCollections(
  client: ServiceClient,
  signal?: AbortSignal,
): Promise<FaqCollectionSummary[]> {
  const rows: FaqCollectionSummary[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < 20; page++) {
    const v: unknown = await client.get<unknown>('admin/content-collections', {
      params: { limit: 50, ...(cursor ? { cursor } : {}) },
      signal,
      responseEnvelope: rawEnvelope,
    });
    if (!record(v) || !Array.isArray(v.data) || !('next_cursor' in v) || !text(v.next_cursor))
      throw invalid();
    for (const row of v.data) {
      if (
        !record(row) ||
        !exact(row, ['id', 'name', 'revision', 'language', 'sort_order']) ||
        !isUuid(row.id) ||
        !text(row.name) ||
        !positive(row.revision) ||
        !['ko', 'en', 'und'].includes(String(row.language)) ||
        !Number.isSafeInteger(row.sort_order)
      )
        throw invalid();
      rows.push(row as unknown as FaqCollectionSummary);
    }
    cursor = v.next_cursor;
    if (!cursor) return rows;
  }
  throw invalid(); // 끝을 확인하지 못한 목록을 전체처럼 보이지 않는다.
}

/** 영향 지점 이름을 보여 주기 위한 발견 조회. 이름만 쓰고 권한·차수의 근거로 쓰지 않는다. */
export async function readFaqPropertyNames(
  client: ServiceClient,
  signal?: AbortSignal,
): Promise<Map<string, string>> {
  const names = new Map<string, string>();
  let cursor: string | null = null;
  for (let page = 0; page < 10; page++) {
    const v: unknown = await client.get<unknown>('admin/content-properties', {
      params: { limit: 50, ...(cursor ? { cursor } : {}) },
      signal,
      responseEnvelope: rawEnvelope,
    });
    const rows = record(v) && Array.isArray(v.data) ? v.data : null;
    if (!rows) throw invalid();
    for (const row of rows) {
      if (!record(row) || !isUuid(row.id) || typeof row.name !== 'string') throw invalid();
      names.set(row.id.toLowerCase(), row.name);
    }
    cursor = record(v) && typeof v.next_cursor === 'string' ? v.next_cursor : null;
    if (!cursor) break;
  }
  return names;
}
